import React, {useState} from 'react';
import type {TableColumnsType} from 'antd';
import {Button, Table} from 'antd';
import {OverallStatus, PODReviewStatus} from "../../../../enums/DeliveryEnum";
import {formatTimestampToDateTime, formatISODateTime} from "../../../../utils/dateTimeUtils";
import {useQuery} from "@apollo/client";
import {allChannelResponse, GET_ALL_CHANNEL} from "../../../../graphql/queries/getChannelAll";
import {OrderType} from "../../../../graphql/types/OrderType";
import { useDspOptions } from '../../../../hooks/useDspOptions';
import {
    ExceptionDictionaryResponse,
    ExceptionDictionaryType,
    GET_ALL_EXCEPTION
} from "../graphql/queries/getAllException";
import { t } from '../../../../i18n';
import { useStatusInternationalization } from '../hooks/useStatusInternationalization';

interface ParcelRowProps {
    parcelData: OrderType[];
    showParcelDetail: (record: OrderType) => void;
    onSelectChange?: (selectedRowKeys: React.Key[], selectedRows: OrderType[]) => void;
    selectedRowKeys?: React.Key[];
    loading?: boolean;
    onSortChange?: (field: string | null, direction: 'ASC' | 'DESC' | null) => void;
    sortField?: string | null;
    sortDirection?: 'ASC' | 'DESC' | null;
    /**
     * 需要隐藏的列 dataIndex 集合。
     * 历史包裹查询用它隐藏「异常状态」列——该列来自 exception_order 表，
     * 历史链路不读该表，值恒为空，显示一个永远空白的列会误导使用者。
     */
    hiddenColumnKeys?: string[];
}

export const ParcelColumns: React.FC<ParcelRowProps> = ({
    parcelData,
    showParcelDetail,
    onSelectChange,
    selectedRowKeys: externalSelectedRowKeys,
    loading,
    onSortChange,
    sortField,
    sortDirection,
    hiddenColumnKeys
}) => {
    const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState<React.Key[]>([]);
    const selectedRowKeys = externalSelectedRowKeys !== undefined ? externalSelectedRowKeys : internalSelectedRowKeys;
    const {data} = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const {data:exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const { dspOptions } = useDspOptions();
    
    // 使用状态国际化hook
    const {
        getStatusText,
        getStatusColor,
        getComplaintText,
        getExceptionText,
        getExceptionStatusText,
        getExceptionReasonText,
        getNextActionTextFromBackend
    } = useStatusInternationalization();

    const handleSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: OrderType[]) => {
        if (onSelectChange) {
            // 如果提供了外部处理函数，调用它
            onSelectChange(newSelectedRowKeys, selectedRows);
        } else {
            // 否则使用内部状态
            setInternalSelectedRowKeys(newSelectedRowKeys);
        }
    };

    // 配置行选择
    const rowSelection = {
        selectedRowKeys,
        onChange: handleSelectChange,
    };

    const columnSortFieldMap: Record<string, string> = {
        receivedAtHub: 'receivedAtHub',
        destHubArrivedAt: 'destHubArrivedAt',
        destWarehouseArrivedAt: 'destWarehouseArrivedAt',
        deliverAt: 'deliverAt',
        lastDeliveryScanTime: 'lastDeliveryScanTime',
        lastDeliveryScanTime2: 'lastDeliveryScanTime',
        deliveredAt: 'deliveredAt',
        orderTime: 'orderTime',
        markExceptionAt: 'markExceptionAt',
        updatedAt: 'updatedAt',
        returnArrivalHubAt: 'returnArrivalHubAt',
    };

    const getSortOrder = (columnKey: string) => {
        const field = columnSortFieldMap[columnKey];
        if (!field || !sortField || field !== sortField || !sortDirection) return undefined;
        return sortDirection === 'ASC' ? 'ascend' : sortDirection === 'DESC' ? 'descend' : undefined;
    };

    const parcelColumns: TableColumnsType<OrderType> = [
        {
            title: t('delivery.parcels.label.tracking'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            align: 'left',
            width: 180,
            ellipsis: true,
            render: (text) => <Button
                type="link"
                onClick={() => showParcelDetail(parcelData.find(p => p.trackingNumber === text)!)}
                style={{ paddingLeft: 0, whiteSpace: 'nowrap' }}
            >
                {text}
            </Button>,
        },
        {
            title: t('delivery.parcels.label.externalTrackingNumber'),
            dataIndex: 'externalTrackingNumber',
            key: 'externalTrackingNumber',
            align: 'left',
            width: 180,
            ellipsis: true,
            render: (text: string | null | undefined) => (
                <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
            ),
        },
        {
            title: t('delivery.parcels.label.order'),
            dataIndex: 'channelOrderNumber',
            key: 'channelOrderNumber',
            align: 'left',
            width: 160,
            ellipsis: true,
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.client'),
            dataIndex: 'channelId',
            key: 'channelId',
            align: 'left',
            width: 160,
            ellipsis: true,
            render: (text, record) => {
                const channel = data?.getAllChannels?.find(
                    (channel) => String(channel.id) === String(record.channelId)
                );
                return <span style={{ whiteSpace: 'nowrap' }}>{channel ? channel.channelName : '-'}</span>;
            },
        },
        {
            title: t('delivery.parcels.label.status'),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            align: 'left',
            width: 140,
            ellipsis: true,
            render: (status) => {
                const statusText = getStatusText(status);
                const color = getStatusColor(status);
                
                return <span className={`inline-block px-2 py-1 rounded ${color}`} style={{ whiteSpace: 'nowrap' }}>{statusText}</span>;
            },
        },
        {
            title: t('delivery.parcels.label.podReviewStatus'),
            dataIndex: 'podReviewStatus',
            key: 'podReviewStatus',
            align: 'left',
            width: 180,
            ellipsis: true,
            render: (value) => {
                if (value === undefined || value === null) return <span style={{ whiteSpace: 'nowrap' }}>-</span>;
                const statusMap = {
                    [PODReviewStatus.NOT_REVIEWED]: t('enum.podReviewStatus.NOT_REVIEWED'),
                    [PODReviewStatus.MACHINE_APPROVED]: t('enum.podReviewStatus.MACHINE_APPROVED'),
                    [PODReviewStatus.MACHINE_DISAPPROVED]: t('enum.podReviewStatus.MACHINE_DISAPPROVED'),
                    [PODReviewStatus.MANUAL_PENDING]: t('enum.podReviewStatus.MANUAL_PENDING'),
                    [PODReviewStatus.MANUAL_APPROVED]: t('enum.podReviewStatus.MANUAL_APPROVED'),
                    [PODReviewStatus.MANUAL_DISAPPROVED]: t('enum.podReviewStatus.MANUAL_DISAPPROVED'),
                    //特殊处理 人审误判修正视作人审通过  展示也作为人审通过展示
                    [PODReviewStatus.MANUAL_CORRECTED]: t('enum.podReviewStatus.MANUAL_APPROVED')
                };
                return <span style={{ whiteSpace: 'nowrap' }}>{statusMap[value as keyof typeof statusMap] || t('enum.podReviewStatus.UNKNOWN')}</span>;
            },
        },
        {
            title: t('delivery.parcels.label.prePickupHub'),
            dataIndex: 'prePickupHubName',
            key: 'prePickupHubName',
            align: 'left',
            width: 160,
            ellipsis: true,
            render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{value || '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.pickupHub'),
            dataIndex: 'pickupHubName',
            key: 'pickupHubName',
            align: 'left',
            width: 160,
            ellipsis: true,
            render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{value || '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.firstSortCode'),
            key: 'sortingCodeSegment1',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (_,record) => {
                const parts = record.sortingCode?.split('-') || [];
                return <span style={{ whiteSpace: 'nowrap' }}>{parts[0] || ''}</span>;
            },
        },
        {
            title: t('delivery.parcels.label.secondSortCode'),
            key: 'sortingCodeSegment2',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (_,record) => {
                const parts = record.sortingCode?.split('-') || [];
                return <span style={{ whiteSpace: 'nowrap' }}>{parts[1] || ''}</span>;
            },
        },
        {
            title: t('delivery.parcels.label.thirdSortCode'),
            key: 'sortingCodeSegment3',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (_,record) => {
                const parts = record.sortingCode?.split('-') || [];
                return <span style={{ whiteSpace: 'nowrap' }}>{parts[2] || ''}</span>;
            },
        },
        {
            title: t('delivery.parcels.label.receiverZipcode'),
            dataIndex: 'receiverZipcode',
            key: 'receiverZipcode',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.dsp'),
            dataIndex: 'deliveredDspId',
            key: 'deliveredDspId',
            align: 'left',
            width: 140,
            ellipsis: true,
            render: (dspId) => {
                if (!dspId) return <span style={{ whiteSpace: 'nowrap' }}>-</span>;
                const dsp = dspOptions?.find(option =>
                    String(option.value) === String(dspId)
                );
                return <span style={{ whiteSpace: 'nowrap' }}>{dsp ? `${dsp.label}` : '-'}</span>;
            },
        },
        {
            title: t('delivery.parcels.label.pickupTaskDate'),
            dataIndex: 'receivedAtHub',
            key: 'receivedAtHub',
            align: 'left',
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('receivedAtHub'),
            sortDirections: ['ascend', 'descend'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatTimestampToDateTime(text) : '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.hubReceiveDate'),
            dataIndex: 'destHubArrivedAt',
            key: 'destHubArrivedAt',
            align: 'left',
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('destHubArrivedAt'),
            sortDirections: ['ascend', 'descend'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatTimestampToDateTime(text) : '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.whsReceiveDate'),
            dataIndex: 'destWarehouseArrivedAt',
            key: 'destWarehouseArrivedAt',
            align: 'left',
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('destWarehouseArrivedAt'),
            sortDirections: ['ascend', 'descend'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatTimestampToDateTime(text) : '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.lastTaskDate'),
            dataIndex: 'deliverAt',
            key: 'deliverAt',
            align: 'left',
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('deliverAt'),
            sortDirections: ['ascend', 'descend'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatTimestampToDateTime(text) : '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.lastDeliveryScanTime'),
            dataIndex: 'lastDeliveryScanTime',
            key: 'lastDeliveryScanTime2',
            align: 'left',
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('lastDeliveryScanTime2'),
            sortDirections: ['ascend', 'descend'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatTimestampToDateTime(text) : '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.deliveredAt'),
            dataIndex: 'deliveredAt',
            key: 'deliveredAt',
            align: 'left',
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('deliveredAt'),
            sortDirections: ['ascend', 'descend'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatTimestampToDateTime(text) : '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.hasException'),
            dataIndex: 'isHasBeenSetException',
            key: 'isHasBeenSetException',
            align: 'left',
            width: 140,
            ellipsis: true,
            render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{getExceptionText(value)}</span>,
        },
        {
            title: t('delivery.parcels.label.exceptionStaus'),
            dataIndex: 'isAbnormal',
            key: 'isAbnormal',
            align: 'left',
            width: 140,
            ellipsis: true,
            render: (text, record) => {
              if (!record.isHasBeenSetException) {
                return <span style={{ whiteSpace: 'nowrap' }}>-</span>;
              }
              return <span style={{ whiteSpace: 'nowrap' }}>{getExceptionStatusText(text, record.exceptionStatus)}</span>;
            }
        },
        {
            title: t('delivery.parcels.label.lastExceptionReason'),
            dataIndex: 'exceptionReasonCode',
            key: 'exceptionReasonCode',
            align: 'left',
            width: 180,
            ellipsis: true,
            render: (text, record) => {
                const exception = exceptionList?.find(
                    (exception) => String(exception.exceptionReasonCode) === String(record.exceptionReasonCode)
                );
                return <span style={{ whiteSpace: 'nowrap' }}>{getExceptionReasonText(exception, record.exceptionReasonCode)}</span>;
            }
        },
        {
            title: t('delivery.parcels.label.lastExceptionDate'),
            dataIndex: 'markExceptionAt',
            key: 'markExceptionAt',
            align: 'left',
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('markExceptionAt'),
            sortDirections: ['ascend', 'descend'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatTimestampToDateTime(text) : '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.nextAction'),
            dataIndex: 'nextAction',
            key: 'nextAction',
            align: 'left',
            width: 160,
            ellipsis: true,
            render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{getNextActionTextFromBackend(value)}</span>,
        },
        {
            title: t('delivery.parcels.label.orderTime'),
            dataIndex: 'orderTime',
            key: 'orderTime',
            align: 'left',
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('orderTime'),
            sortDirections: ['ascend', 'descend'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatTimestampToDateTime(text) : '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.bagTaskNumber'),
            dataIndex: 'bagTaskNumber',
            key: 'bagTaskNumber',
            align: 'left',
            width: 160,
            ellipsis: true,
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text || ''}</span>,
        },
        {
            title: t('delivery.parcels.label.lastScannedDriver'),
            dataIndex: 'lastScannedDriverName',
            key: 'lastScannedDriverName',
            align: 'left',
            width: 160,
            ellipsis: true,
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text || ''}</span>,
        },
        {
            title: t('delivery.parcels.label.lastAssignedDriver'),
            dataIndex: 'lastAssignedDriverName',
            key: 'lastAssignedDriverName',
            align: 'left',
            width: 160,
            ellipsis: true,
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text || ''}</span>,
        },
        {
            title: t('delivery.parcels.label.isComplaint'),
            dataIndex: 'isComplaint',
            key: 'isComplaint',
            align: 'left',
            width: 120,
            ellipsis: true,
            render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{getComplaintText(value)}</span>,
        },
        {
            title: t('delivery.parcels.label.lastUpdatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            align: 'left',
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('updatedAt'),
            sortDirections: ['ascend', 'descend'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatISODateTime(text) : '-'}</span>,
        },
        {
            title: t('delivery.parcels.label.returnArrivalHubAt'),
            dataIndex: 'returnArrivalHubAt',
            key: 'returnArrivalHubAt',
            align: 'left',
            width: 220,
            ellipsis: true,
            sorter: true,
            sortOrder: getSortOrder('returnArrivalHubAt'),
            sortDirections: ['ascend', 'descend'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text ? formatTimestampToDateTime(text) : '-'}</span>,
        }
    ];

    /**
     * 按 hiddenColumnKeys 过滤掉不展示的列。
     * 历史包裹查询用它去掉「异常状态」——该列取自 exception_order 表，历史链路不读，
     * 留着就是一列永远空白的数据，比不显示更容易误导。
     */
    const visibleColumns = (hiddenColumnKeys && hiddenColumnKeys.length > 0)
        ? parcelColumns.filter((col: any) => !hiddenColumnKeys.includes(String(col?.dataIndex ?? col?.key ?? '')))
        : parcelColumns;

    const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
        if (!onSortChange) return;
        const order = sorter?.order as ('ascend' | 'descend' | undefined);
        const columnKey = sorter?.columnKey as (string | undefined);
        if (!order || !columnKey) {
            onSortChange(null, null);
            return;
        }
        // 映射列key到后端字段
        const field = columnSortFieldMap[columnKey] || 'orderTime';
        const direction = order === 'ascend' ? 'ASC' : 'DESC';
        onSortChange(field, direction);
    };

    return (
        <Table
            rowSelection={rowSelection}
            rowKey="trackingNumber"
            columns={visibleColumns}
            dataSource={parcelData}
            scroll={{x: 'max-content'}}
            size="middle"
            className="mb-4"
            pagination={false}
            loading={loading}
            onChange={handleTableChange}
            tableLayout="fixed"
        />
    );
};